import { useState, useEffect, useCallback } from 'react';
import { Building2, FileText, Layers, Hash, DollarSign, Plus, Eye, ExternalLink, Phone, Mail, ChevronDown, Loader2, Calendar, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { InlineNotesTasksSection } from './NotesTasksSection';
import { viewQuotePdf } from '../../utils/viewQuotePdf';
import { productColour } from '../AddToCrm/constants';
import { ContactNotesTasksModal } from '../ContactNotesTasksModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ENCON_ORANGE = '#ee7203';

const PACKAGE_STATUSES = {
  Contractor_Packages: [
    "Tender",
    "Quoted",
    "In Progress",
    "Contractor Successful",
    "Contractor Lost",
  ],
  Sub_Contractor_Packages: [
    "Tender",
    "Quoted",
    "In Progress",
    "Sub Contractor Successful",
    "Sub Contractor Lost",
  ],
};

const PKG_STATUS_STYLE = {
  "Tender":                    { bg: '#f1f5f9', color: '#475569' },
  "Quoted":                    { bg: '#fff7ed', color: '#c2410c' },
  "In Progress":               { bg: '#eff6ff', color: '#1d4ed8' },
  "Contractor Successful":     { bg: '#dcfce7', color: '#166534' },
  "Sub Contractor Successful": { bg: '#dcfce7', color: '#166534' },
  "Contractor Lost":           { bg: '#fee2e2', color: '#b91c1c' },
  "Sub Contractor Lost":       { bg: '#fee2e2', color: '#b91c1c' },
};

function PackageStatusSelect({ pkg }) {
  const [status, setStatus]   = useState(pkg.status || '');
  const [saving, setSaving]   = useState(false);
  const module = pkg._module;

  const handleChange = async (newStatus) => {
    if (newStatus === status || saving || !pkg.id) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/zoho/package/${module}/${pkg.id}/status`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error('Failed');
      setStatus(newStatus);
      toast.success('Package status updated');
    } catch {
      toast.error('Failed to update package status');
    } finally {
      setSaving(false);
    }
  };

  const options = PACKAGE_STATUSES[module] || PACKAGE_STATUSES.Contractor_Packages;
  const s = PKG_STATUS_STYLE[status] || { bg: '#f3f4f6', color: '#374151' };

  return (
    <div className="relative flex-shrink-0" data-testid={`pkg-status-select-${pkg.id}`}>
      <select
        value={status}
        onChange={e => handleChange(e.target.value)}
        disabled={saving || !pkg.id}
        className="text-xs font-semibold px-2.5 py-1 rounded-full appearance-none cursor-pointer hover:opacity-80 transition-opacity pr-6 border border-gray-200 outline-none disabled:opacity-60"
        style={{ backgroundColor: s.bg, color: s.color }}
      >
        {!status && <option value="">— status —</option>}
        {options.map(st => (
          <option key={st} value={st} style={{ backgroundColor: '#fff', color: '#111' }}>{st}</option>
        ))}
      </select>
      {saving
        ? <Loader2 className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 animate-spin" style={{ color: s.color }} />
        : <ChevronDown className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3" style={{ color: s.color }} />
      }
    </div>
  );
}

const QUOTE_STAGES = [
  "Negotiation Tender",
  "Negotiation Order to Place",
  "Awaiting Supplier Information",
  "On Hold",
  "Closed Won Order in Progress",
  "Closed Won Order Completed",
  "Closed Lost",
];

const STAGE_STYLE = {
  "Negotiation Tender":              { bg: '#f1f5f9', color: '#475569' },
  "Negotiation Order to Place":      { bg: '#fff7ed', color: '#c2410c' },
  "Awaiting Supplier Information":   { bg: '#fefce8', color: '#92400e' },
  "On Hold":                         { bg: '#f3f4f6', color: '#6b7280' },
  "Closed Won Order in Progress":    { bg: '#dcfce7', color: '#15803d' },
  "Closed Won Order Completed":      { bg: '#bbf7d0', color: '#166534' },
  "Closed Lost":                     { bg: '#fee2e2', color: '#b91c1c' },
};

// Derive initial outcome from existing package status
function statusToOutcome(status, module) {
  if (!status) return null;
  const s = status.toLowerCase();
  if (s.includes('successful')) return 'won';
  if (s.includes('lost')) return 'lost';
  return null;
}

function PackageOutcomeSelect({ pkg, onOutcomeChange }) {
  const [outcome, setOutcome]           = useState(() => statusToOutcome(pkg.status, pkg._module));
  const [saving, setSaving]             = useState(false);
  const [showLostModal, setShowLostModal] = useState(false);
  const [lostReason, setLostReason]     = useState('');
  const [open, setOpen]                 = useState(false);
  const module = pkg._module;

  const commit = useCallback(async (newOutcome, reason) => {
    if (!pkg.id) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const body = { outcome: newOutcome };
      if (newOutcome === 'lost' && reason) body.reason_for_loss = reason;
      const res = await fetch(`${API}/zoho/package/${module}/${pkg.id}/outcome`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setOutcome(newOutcome);
      onOutcomeChange?.(newOutcome, data.status);
      toast.success(newOutcome === 'won' ? 'Marked as Won!' : 'Marked as Lost');
    } catch {
      toast.error('Failed to update package outcome');
    } finally {
      setSaving(false);
    }
  }, [pkg.id, module, onOutcomeChange]);

  const pill =
    outcome === 'won'  ? { label: 'Won',    bg: '#16a34a', color: '#fff' } :
    outcome === 'lost' ? { label: 'Lost',   bg: '#dc2626', color: '#fff' } :
                         { label: 'Status', bg: '#f3f4f6', color: '#6b7280' };

  return (
    <>
      <div className="relative flex-shrink-0">
        <button
          onClick={() => setOpen(o => !o)}
          disabled={saving}
          className="flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full transition-opacity hover:opacity-80 disabled:opacity-60"
          style={{ backgroundColor: pill.bg, color: pill.color }}
          data-testid={`pkg-outcome-btn-${pkg.id}`}
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : pill.label}
          <ChevronDown className="w-3 h-3" />
        </button>
        {open && (
          <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 min-w-[110px] overflow-hidden">
            <button
              onClick={() => { setOpen(false); commit('won', null); }}
              className="w-full text-left px-3 py-2 text-sm font-semibold text-green-700 hover:bg-green-50 flex items-center gap-2"
            >
              <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" /> Won
            </button>
            <button
              onClick={() => { setOpen(false); setShowLostModal(true); }}
              className="w-full text-left px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 flex items-center gap-2"
            >
              <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" /> Lost
            </button>
            {outcome && (
              <button
                onClick={() => { setOpen(false); setOutcome(null); onOutcomeChange?.(null, null); }}
                className="w-full text-left px-3 py-2 text-xs text-gray-400 hover:bg-gray-50 border-t border-gray-100"
              >
                Clear
              </button>
            )}
          </div>
        )}
      </div>

      {/* Lost reason modal */}
      {showLostModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40" onClick={() => setShowLostModal(false)}>
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-gray-900 mb-1">Reason for Loss</h3>
            <p className="text-sm text-gray-500 mb-4">This will be saved to the Reason for Loss field in Zoho CRM and the confidence will be set to 0%.</p>
            <textarea
              autoFocus
              value={lostReason}
              onChange={e => setLostReason(e.target.value)}
              rows={4}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-400"
              placeholder="Describe why this package was lost..."
              data-testid="pkg-lost-reason-input"
            />
            <div className="flex gap-2 mt-4 justify-end">
              <button onClick={() => setShowLostModal(false)} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50">Cancel</button>
              <button
                onClick={() => { setShowLostModal(false); commit('lost', lostReason); setLostReason(''); }}
                className="px-4 py-2 text-sm rounded-xl bg-red-600 text-white font-semibold hover:bg-red-700"
                data-testid="pkg-lost-confirm-btn"
              >
                Confirm Lost
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


function QuoteStageSelect({ quoteId, initialStage }) {
  const [stage, setStage]       = useState(initialStage || '');
  const [saving, setSaving]     = useState(false);

  const handleChange = useCallback(async (newStage) => {
    if (newStage === stage || saving) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/zoho/quotes/${quoteId}/stage`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ stage: newStage }),
      });
      if (!res.ok) throw new Error('Failed');
      setStage(newStage);
      toast.success('Quote stage updated');
    } catch {
      toast.error('Failed to update stage');
    } finally {
      setSaving(false);
    }
  }, [quoteId, stage, saving]);

  const s = STAGE_STYLE[stage] || { bg: '#f1f5f9', color: '#475569' };

  return (
    <div className="relative flex-shrink-0" data-testid={`quote-stage-select-${quoteId}`}>
      <select
        value={stage}
        onChange={e => handleChange(e.target.value)}
        disabled={saving}
        className="text-xs font-semibold px-2.5 py-0.5 rounded-full appearance-none cursor-pointer hover:opacity-80 transition-opacity pr-6 border-0 outline-none disabled:opacity-60"
        style={{ backgroundColor: s.bg, color: s.color }}
      >
        {!stage && <option value="">— stage —</option>}
        {QUOTE_STAGES.map(st => (
          <option key={st} value={st} style={{ backgroundColor: '#fff', color: '#111' }}>{st}</option>
        ))}
      </select>
      {saving
        ? <Loader2 className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 animate-spin" style={{ color: s.color }} />
        : <ChevronDown className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3" style={{ color: s.color }} />
      }
    </div>
  );
}

const ROLE_STYLES = {
  Contractor_Packages: {
    label: 'Main Contractor',
    headerBg: 'bg-orange-50',
    headerBorder: 'border-b-2 border-orange-200',
    cardBorder: 'border-2 border-orange-200',
    badgeCls: 'bg-orange-100 text-orange-800',
    defaultCardBg: '#fff',
  },
  Sub_Contractor_Packages: {
    label: 'Sub Contractor',
    headerBg: 'bg-purple-50',
    headerBorder: 'border-b-2 border-purple-200',
    cardBorder: 'border-2 border-purple-200',
    badgeCls: 'bg-purple-100 text-purple-800',
    defaultCardBg: '#faf7ff',
  },
};

function fmtVal(v) {
  if (!v && v !== 0) return null;
  const n = parseFloat(v);
  return isNaN(n) ? null : `£${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
}

// Format date from yyyy-mm-dd to dd/mm/yyyy
function formatDateDisplay(dateStr) {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
}

// Convert dd/mm/yyyy to yyyy-mm-dd for input
function formatDateForInput(dateStr) {
  if (!dateStr) return '';
  if (dateStr.includes('-')) return dateStr; // already in yyyy-mm-dd format
  const [day, month, year] = dateStr.split('/');
  return `${year}-${month}-${day}`;
}

export default function PackageCard({ pkg, project, onAddNote, onAddTask, onAddQuote }) {
  const [refreshNotesTick] = useState(0);
  const [refreshTasksTick] = useState(0);
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState('');
  const [customerModalAccount, setCustomerModalAccount] = useState(null);

  // Won / Lost outcome state — drives card-level colour
  const [outcome, setOutcome] = useState(() => statusToOutcome(pkg.status, pkg._module));

  // Local copy of quotes so we can reflect "Closed Lost" instantly when the
  // package outcome is set to Lost, without waiting for a page refresh.
  const [localQuotes, setLocalQuotes] = useState(pkg.quotes || []);

  const handleOutcomeChange = useCallback((newOutcome, newStatus) => {
    setOutcome(newOutcome);
    if (newOutcome === 'lost') {
      setLocalQuotes(prev => prev.map(q => ({ ...q, stage: 'Closed Lost' })));
    }
  }, []);

  // Package type state
  const [localPkgType, setLocalPkgType] = useState(pkg.package_type || '');
  const [pkgTypes, setPkgTypes] = useState([]);
  const [pkgTypeUpdating, setPkgTypeUpdating] = useState(false);

  // Encon start date state
  const [enconStartDate, setEnconStartDate] = useState(pkg.encon_start_date || '');
  const [editingEnconDate, setEditingEnconDate] = useState(false);
  const [enconDateInput, setEnconDateInput] = useState(pkg.encon_start_date || '');
  const [enconDateSaving, setEnconDateSaving] = useState(false);

  // Barbour start date from project context
  const barbourStartDate = project?.barbour_start_date || '';

  const module = pkg._module;
  const style = ROLE_STYLES[module] || {
    label: module, headerBg: 'bg-gray-50', headerBorder: 'border-b border-gray-200',
    cardBorder: 'border-2 border-gray-200', badgeCls: 'bg-gray-100 text-gray-700',
  };
  const accountName = module === 'Contractor_Packages'
    ? pkg.contractor_account_name
    : pkg.sub_contractor_account_name;

  const accountId = module === 'Contractor_Packages'
    ? pkg.contractor_account_id
    : pkg.sub_contractor_account_id;

  const crmUrl = pkg.id ? `https://crm.zoho.eu/crm/encon/tab/${module}/${pkg.id}` : null;
  const categories = Array.isArray(pkg.package_category)
    ? pkg.package_category : pkg.package_category ? [pkg.package_category] : [];
  const quotes = localQuotes;
  const pkgColor = localPkgType ? productColour(localPkgType) : '#9ca3af';

  // Fetch contacts for this package's account
  useEffect(() => {
    if (!accountId) return;
    setContactsLoading(true);
    const token = localStorage.getItem('token');
    fetch(`${API}/zoho/account/${accountId}/contacts`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        const list = data.contacts || [];
        setContacts(list);
        if (list.length > 0) setSelectedContactId(list[0].id);
      })
      .catch(() => {})
      .finally(() => setContactsLoading(false));
  }, [accountId]);

  // Fetch available package types
  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API}/zoho/package-types`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        const types = module === 'Contractor_Packages'
          ? (data.contractor_package_types || [])
          : (data.sub_contractor_package_types || []);
        setPkgTypes(types);
      })
      .catch(() => {});
  }, [module]);

  const handlePackageTypeChange = async (newType) => {
    if (newType === localPkgType || pkgTypeUpdating || !newType) return;
    setPkgTypeUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/zoho/package/${module}/${pkg.id}/type`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ package_type: newType }),
      });
      if (!res.ok) throw new Error('Failed');
      setLocalPkgType(newType);
      toast.success('Package type updated');
    } catch {
      toast.error('Failed to update package type');
    } finally {
      setPkgTypeUpdating(false);
    }
  };

  const handleEnconDateSave = async () => {
    if (!enconDateInput) return;
    setEnconDateSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/zoho/package/${module}/${pkg.id}/start-date`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ encon_start_date: enconDateInput }),
      });
      if (!res.ok) throw new Error('Failed');
      setEnconStartDate(enconDateInput);
      setEditingEnconDate(false);
      toast.success('Encon start date updated');
    } catch {
      toast.error('Failed to update start date');
    } finally {
      setEnconDateSaving(false);
    }
  };

  const selectedContact = contacts.find(c => c.id === selectedContactId) || null;
  const contactPhone = selectedContact?.mobile || selectedContact?.phone || null;
  const contactEmail = selectedContact?.email || null;

  // Build dropdown options: always include current value even if not in list
  const typeOptions = pkgTypes.length > 0
    ? [...new Set([...(localPkgType ? [localPkgType] : []), ...pkgTypes])]
    : localPkgType ? [localPkgType] : [];

  // Compute card-level styling driven by outcome
  const outcomeCardStyle =
    outcome === 'won'  ? { border: '2px solid #16a34a', backgroundColor: '#f0fdf4' } :
    outcome === 'lost' ? { border: '2px solid #dc2626', backgroundColor: '#fff5f5' } :
    module === 'Sub_Contractor_Packages' ? { backgroundColor: '#faf7ff' } : {};

  return (
    <>
      <div
        className={`rounded-2xl shadow overflow-hidden package-card ${outcome ? '' : style.cardBorder}`}
        style={outcomeCardStyle}
        data-testid={`pt-package-card-${pkg.id}`}
      >

        {/* ── Badge header ─────────────────────────────────────── */}
        <div
          className={`px-4 py-2 flex items-center gap-2 flex-wrap ${outcome === 'won' ? 'bg-green-100 border-b-2 border-green-300' : outcome === 'lost' ? 'bg-red-100 border-b-2 border-red-300' : `${style.headerBg} ${style.headerBorder}`}`}
        >
          {/* Account name (clickable → opens Customer Management modal) */}
          {accountName ? (
            <button
              type="button"
              onClick={() => setCustomerModalAccount({ id: accountId, name: accountName })}
              className="flex items-center gap-1 text-sm font-bold text-gray-900 min-w-0 truncate hover:text-blue-700 hover:underline transition-colors"
              title="Open Customer Management"
              data-testid={`pt-pkg-customer-btn-${pkg.id}`}
            >
              <Building2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              <span className="truncate">{accountName}</span>
            </button>
          ) : null}

          {/* Contractor type pill — placed AFTER the account name */}
          <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${style.badgeCls} flex-shrink-0`}>
            {style.label}
          </span>

          {/* Package type — editable dropdown pill */}
          {typeOptions.length > 0 && (
            <div className="relative flex-shrink-0">
              <select
                value={localPkgType}
                onChange={e => handlePackageTypeChange(e.target.value)}
                disabled={pkgTypeUpdating}
                className="text-xs font-bold px-2.5 py-0.5 rounded-full text-white appearance-none cursor-pointer hover:opacity-80 transition-opacity pr-5 border-0 outline-none"
                style={{ backgroundColor: pkgColor }}
                data-testid={`pt-pkg-type-select-${pkg.id}`}
              >
                {!localPkgType && <option value="">— type —</option>}
                {typeOptions.map(t => (
                  <option key={t} value={t} style={{ color: '#111', backgroundColor: '#fff' }}>{t}</option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-white" />
              {pkgTypeUpdating && <Loader2 className="absolute left-1 top-1/2 -translate-y-1/2 w-3 h-3 text-white animate-spin" />}
            </div>
          )}

          <PackageStatusSelect pkg={pkg} />
          <PackageOutcomeSelect pkg={pkg} onOutcomeChange={handleOutcomeChange} />

          {/* ── Encon Start Date pill ─────────────────────────────────── */}
          <div className="flex items-center flex-shrink-0">
            {editingEnconDate ? (
              <div className="flex flex-col gap-1">
                <span className="text-[10px] text-gray-500 font-medium">Encon start date (dd/mm/yyyy)</span>
                <div className="flex items-center gap-1">
                  <input
                    type="date"
                    value={enconDateInput}
                    onChange={e => setEnconDateInput(e.target.value)}
                    className="text-xs border border-blue-300 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                    autoFocus
                    data-testid={`pkg-encon-date-input-${pkg.id}`}
                  />
                  <button
                    type="button"
                    onClick={handleEnconDateSave}
                    disabled={enconDateSaving || !enconDateInput}
                    className="p-0.5 rounded text-emerald-600 hover:bg-emerald-50 disabled:opacity-50"
                    title="Save"
                  >
                    {enconDateSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setEditingEnconDate(false); setEnconDateInput(enconStartDate); }}
                    className="p-0.5 rounded text-gray-400 hover:bg-gray-100"
                    title="Cancel"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => { setEditingEnconDate(true); setEnconDateInput(enconStartDate || barbourStartDate); }}
                className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border transition-colors hover:opacity-80 ${
                  enconStartDate
                    ? 'bg-blue-50 border-blue-200 text-blue-700'
                    : barbourStartDate
                    ? 'bg-gray-50 border-gray-200 text-gray-500 italic'
                    : 'bg-gray-50 border-dashed border-gray-300 text-gray-400'
                }`}
                title={enconStartDate ? 'Encon start date (click to edit)' : barbourStartDate ? 'Barbour start date — click to set Encon date' : 'Click to set Encon start date'}
                data-testid={`pkg-encon-date-pill-${pkg.id}`}
              >
                <Calendar className="w-3 h-3 flex-shrink-0" />
                {enconStartDate
                  ? formatDateDisplay(enconStartDate)
                  : barbourStartDate
                  ? formatDateDisplay(barbourStartDate)
                  : 'Start date'}
              </button>
            )}
          </div>

          <div className="ml-auto flex items-center gap-1 flex-shrink-0">
            {onAddQuote && (
              <button onClick={onAddQuote}
                className="flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded hover:opacity-80 print:hidden"
                style={{ backgroundColor: '#fff3e6', color: ENCON_ORANGE }}
                data-testid={`pt-pkg-add-quote-${pkg.id}`}>
                <Plus className="w-3 h-3" /> Quote
              </button>
            )}
            {crmUrl && (
              <a href={crmUrl} target="_blank" rel="noreferrer"
                className="p-1 rounded hover:bg-white/60 transition-colors print:hidden" title="Open in Zoho">
                <ExternalLink className="w-3.5 h-3.5 text-gray-400" />
              </a>
            )}
          </div>
        </div>

        {/* ── Main body: 1/4 contact info + 3/4 notes & tasks ── */}
        <div className="grid min-h-[160px]" style={{ gridTemplateColumns: '1fr 3fr' }}>

          {/* 1/4 — Contact details + dropdown */}
          <div className="p-4 space-y-2.5 border-r-2 border-gray-100">

            {/* Contacts dropdown */}
            {contactsLoading ? (
              <div className="flex items-center gap-1.5 text-xs text-gray-400">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading…
              </div>
            ) : contacts.length === 0 ? (
              <p className="text-xs text-gray-500 italic">No contacts found</p>
            ) : (
              <div className="relative">
                <select
                  value={selectedContactId}
                  onChange={e => setSelectedContactId(e.target.value)}
                  className="w-full text-xs font-semibold text-gray-900 bg-white border border-gray-200 rounded-lg px-2 py-1.5 pr-6 appearance-none focus:outline-none focus:ring-2 focus:ring-orange-300 truncate cursor-pointer"
                  data-testid={`pt-pkg-contact-select-${pkg.id}`}
                >
                  {contacts.map(c => (
                    <option key={c.id} value={c.id}>{c.full_name}</option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
              </div>
            )}

            {/* Selected contact's phone */}
            {contactPhone && (
              <div className="flex items-center gap-1.5">
                <Phone className="w-3 h-3 text-gray-400 flex-shrink-0" />
                <a
                  href={`tel:${contactPhone}`}
                  className="text-xs text-gray-900 hover:text-blue-700 transition-colors truncate"
                  data-testid={`pt-pkg-contact-phone-${pkg.id}`}
                >
                  {contactPhone}
                </a>
              </div>
            )}

            {/* Selected contact's email */}
            {contactEmail && (
              <div className="flex items-center gap-1.5">
                <Mail className="w-3 h-3 text-gray-400 flex-shrink-0" />
                <a
                  href={`mailto:${contactEmail}`}
                  className="text-xs text-gray-900 hover:text-blue-700 transition-colors truncate"
                  title={contactEmail}
                  data-testid={`pt-pkg-contact-email-${pkg.id}`}
                >
                  {contactEmail}
                </a>
              </div>
            )}

            {/* Categories */}
            {categories.length > 0 && (
              <div className="flex items-start gap-1.5">
                <Layers className="w-3 h-3 text-gray-400 flex-shrink-0 mt-0.5" />
                <span className="text-xs text-gray-700 leading-snug">{categories.join(', ')}</span>
              </div>
            )}

            {/* Parent package link (sub-contractor only) */}
            {module === 'Sub_Contractor_Packages' && pkg.parent_contractor_package_name && (
              <div className="flex items-center gap-1.5">
                <Hash className="w-3 h-3 text-gray-400 flex-shrink-0" />
                <span className="text-xs text-gray-700">Under: {pkg.parent_contractor_package_name}</span>
              </div>
            )}

            {/* Package amount */}
            {fmtVal(pkg.amount) && (
              <div className="flex items-center gap-1.5">
                <DollarSign className="w-3 h-3 text-gray-400 flex-shrink-0" />
                <span className="text-sm font-semibold" style={{ color: ENCON_ORANGE }}>{fmtVal(pkg.amount)}</span>
              </div>
            )}
          </div>

          {/* 3/4 — Notes + Tasks side by side */}
          <div>
            <InlineNotesTasksSection
              horizontal
              fetchNotesUrl={`${API}/zoho/package/${module}/${pkg.id}/notes`}
              fetchTasksUrl={`${API}/zoho/package/${module}/${pkg.id}/tasks`}
              onAddNote={onAddNote}
              onAddTask={onAddTask}
              refreshNotesTick={refreshNotesTick}
              refreshTasksTick={refreshTasksTick}
            />
          </div>
        </div>

        {/* ── Full-width quote rows ─────────────────────────────── */}
        {quotes.length > 0 && (
          <div className="border-t-2 border-gray-100">
            {quotes.map((q) => (
              <div key={q.id} className="px-4 py-3 border-b border-gray-100 last:border-0">
                {/* Two-column layout: details left, description right */}
                <div className="flex gap-4">
                  {/* LEFT: PDF + stage + value + title + supplier */}
                  <div className="flex-1 min-w-0">
                    {/* PDF + stage + value */}
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      {q.has_pdf && (
                        <button
                          onClick={() => viewQuotePdf(q.id, q.subject)}
                          className="flex items-center gap-1 text-xs font-semibold px-2.5 py-0.5 rounded-full border hover:opacity-80 transition-opacity print:hidden"
                          style={{ borderColor: pkgColor, color: pkgColor, backgroundColor: `${pkgColor}18` }}
                          data-testid={`pt-pkg-quote-pdf-${q.id}`}
                          title="View PDF"
                        >
                          <Eye className="w-3 h-3" /> PDF
                        </button>
                      )}
                      {q.id && (
                        <QuoteStageSelect key={`${q.id}-${q.stage}`} quoteId={q.id} initialStage={q.stage} />
                      )}
                      {!q.id && q.stage && (
                        <span className="text-xs px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium flex-shrink-0">{q.stage}</span>
                      )}
                      {fmtVal(q.value) && (
                        <span className="text-sm font-bold flex-shrink-0" style={{ color: ENCON_ORANGE }}>{fmtVal(q.value)}</span>
                      )}
                    </div>
                    {/* Quote title */}
                    <div className="flex items-start gap-2">
                      <FileText className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 leading-snug">{q.subject || 'Untitled quote'}</p>
                        {q.supplier && <p className="text-sm text-gray-700 mt-0.5 font-medium">Supplier: {q.supplier}</p>}
                      </div>
                    </div>
                  </div>

                  {/* RIGHT: description — one product per line */}
                  {q.description && (
                    <div className="w-[45%] flex-shrink-0 border-l border-gray-100 pl-3 pt-0.5">
                      <div className="space-y-0.5">
                        {q.description.split('\n').filter(l => l.trim()).map((line, i) => (
                          <p key={i} className="text-xs text-gray-700 leading-snug">{line.trim()}</p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Customer Management modal (Zoho CRM) */}
      {customerModalAccount && (
        <ContactNotesTasksModal
          isOpen={!!customerModalAccount}
          onClose={() => setCustomerModalAccount(null)}
          initialAccount={customerModalAccount}
        />
      )}
    </>
  );
}
